import { HotTable, HotColumn } from "@handsontable/react-wrapper";
import React, { useContext, useRef } from "react";
import {
    FinancialDocument,
    ImporterErrors,
} from "../../common/types/financialDocument";
import { useFinancialDocumentReviewMutation } from "../../api/financialDocument.api";
import { SpreadsheetDateEditor } from "../spreadsheet/SpreasheetDateEditor";
import { PersistentSpreadsheet } from "../spreadsheet/PersistentSpreadsheet";
import { ImporterSpreadsheetColumns } from "../../common/helpers/financialDocument";
import styles from "./ImporterPage.module.scss";
import { isTransactionsProcessingFinished } from "./helpers";
import { ImporterBalanceSummary } from "./ImporterBalanceSummary";
import { ImporterContext } from "./importer.context";
import { ImporterSheetTransaction } from "./types";

interface Props {
    documentRowId: string;
    financialDocument: FinancialDocument;
}

export const ImporterDataPreview: React.FC<Props> = ({
    documentRowId,
    financialDocument,
}) => {
    const { updateDocumentRow } = useContext(ImporterContext);
    const reviewed = useRef(false);

    const reviewMutation = useFinancialDocumentReviewMutation(
        financialDocument,
        (document) => {
            updateDocumentRow(documentRowId, {
                financialDocument: document,
            });
        },
    );

    if (!isTransactionsProcessingFinished(financialDocument)) {
        return (
            <p className={styles.transactionsMessage}>
                We're extracting data from this document. Please wait...
            </p>
        );
    }

    if (!financialDocument.numTransactions) {
        return (
            <p className={styles.transactionsMessage}>
                No transactions found in this document
            </p>
        );
    }

    if (
        financialDocument.importerErrors?.includes(
            ImporterErrors.NOT_CONFIDENT,
        ) &&
        !reviewed.current
    ) {
        reviewed.current = true;
        reviewMutation.mutate();
    }

    return financialDocument.transactionsSpreadsheetId ? (
        <PersistentSpreadsheet
            spreadsheetId={financialDocument.transactionsSpreadsheetId}
            accessParams={{
                financialDocumentId: financialDocument.id,
            }}
        >
            {({ data, afterChange }) => (
                <>
                    <ImporterBalanceSummary
                        documentRowId={documentRowId}
                        financialDocument={financialDocument}
                        transactions={data as ImporterSheetTransaction[]}
                    />
                    <div className={styles.transactionsTable}>
                        <HotTable
                            data={data}
                            licenseKey="non-commercial-and-evaluation"
                            autoRowSize={false}
                            rowHeights={30}
                            rowHeaders={true}
                            colHeaders={true}
                            width="100%"
                            height="100%"
                            autoWrapRow={false}
                            autoWrapCol={false}
                            wordWrap={false}
                            minSpareRows={20}
                            fillHandle={true}
                            themeName="ht-theme-kick"
                            afterChange={afterChange}
                        >
                            <HotColumn
                                data={ImporterSpreadsheetColumns.DATE}
                                allowEmpty={true}
                                editor={SpreadsheetDateEditor}
                                title="Date"
                                width={115}
                            />
                            <HotColumn
                                data={ImporterSpreadsheetColumns.DESCRIPTION}
                                type="text"
                                allowEmpty={true}
                                title="Description"
                                width={430}
                            />
                            <HotColumn
                                data={ImporterSpreadsheetColumns.AMOUNT}
                                type="numeric"
                                numericFormat={{
                                    pattern: "$0,0.00",
                                    culture: "en-US",
                                }}
                                className={styles.amountHeader}
                                allowEmpty={true}
                                title="Amount"
                                width={115}
                                id="amount"
                            />
                        </HotTable>
                    </div>
                </>
            )}
        </PersistentSpreadsheet>
    ) : null;
};
